import AccordionSetup from '../AccordionSetup'
import { EventChecklist, EventDto } from '../../../interfaces/IEvent'
import { Box, Collapse, IconButton, Link, Typography, useTheme } from '@mui/material'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckIcon from '@mui/icons-material/Check'
import { EbCheckbox } from '@eidsivabredband/react-component-ui-package'
import Stack from '@mui/material/Stack'
import InputField from '../../../components/InputFields/InputField'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { useContext, useEffect, useState, useRef, ChangeEvent } from 'react'
import { enqueueSnackbar } from 'notistack'
import MonoInputField from '../../../components/InputFields/MonoInputField'
import TypographyWithUrl from '../../../components/data-display/TypographyWithUrls'

interface ChecklistContainerProps {
  viewEvent: EventDto
  onUpdateEventView: (eventId: string) => void
}

function ChecklistContainer({ viewEvent, onUpdateEventView }: ChecklistContainerProps) {
  const { context } = useContext(EbAuthenticatedAppContext)
  const theme = useTheme()
  const [initialLoad, setInitialLoad] = useState<boolean>(false)
  const [collapseVerification, setCollapseVerification] = useState<boolean>()
  const [collapseRollbackplan, setCollapseRollbackplan] = useState<boolean>()
  const [editVerification, setEditVerification] = useState<boolean>(false)
  const [editRollbackPlan, setEditRollbackPlan] = useState<boolean>(false)

  const verificationContentSizeRef = useRef<HTMLDivElement>(null)
  const [verificationContentHeight, setVerificationContentHeight] = useState<number>(0)

  const rollbackPlanContentSizeRef = useRef<HTMLDivElement>(null)
  const [rollbackPlanContentHeight, setRollbackPlanContentHeight] = useState<number>(0)

  const [eventChecklist, setEventChecklist] = useState<EventChecklist>({
    approvedTime: viewEvent.eventDetails?.checklist?.approvedTime ?? false,
    customerListCompleted: viewEvent.eventDetails?.checklist?.customerListCompleted ?? false,
    workCompleted: viewEvent.eventDetails?.checklist?.workCompleted ?? false,
    verification: viewEvent.eventDetails?.checklist?.verification ?? '',
    verificationLink: viewEvent.eventDetails?.checklist?.verificationLink ?? '',
    rollbackPlan: viewEvent.eventDetails?.checklist?.rollbackPlan ?? '',
    rollbackPlanLink: viewEvent.eventDetails?.checklist?.rollbackPlanLink ?? '',
  })

  const handleCollapseVerification = (e: any) => {
    e.stopPropagation()
    setCollapseVerification((prev) => !prev)
  }

  const handleCollapseRollbackPlan = (e: any) => {
    e.stopPropagation()
    setCollapseRollbackplan((prev) => !prev)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setEventChecklist({
      ...eventChecklist,
      [name]: value,
    })
  }

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target

    setEventChecklist({
      ...eventChecklist,
      [name]: checked,
    })
  }

  const handleSubmit = async () => {
    var response = await context.restApiClient
      .buildRequest(`/api/events/${viewEvent.id}/checklist/update`)
      .serializeToJsonContent(eventChecklist)
      .putAsync()

    if (response.error) {
      return enqueueSnackbar(<Typography variant="body2">Klarte ikke oppdatere sjekkliste. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
    }

    onUpdateEventView(viewEvent.id)
  }

  useEffect(() => {
    if (!verificationContentSizeRef.current) return
    setVerificationContentHeight(verificationContentSizeRef.current?.clientHeight || 0)

    if (!rollbackPlanContentSizeRef.current) return
    setRollbackPlanContentHeight(rollbackPlanContentSizeRef.current?.clientHeight || 0)
  }, [verificationContentSizeRef, rollbackPlanContentSizeRef, eventChecklist, editVerification, editRollbackPlan])

  useEffect(() => {
    if (viewEvent.eventDetails.checklist?.verification?.length === 0) {
      setEditVerification(true)
    }
    if (viewEvent.eventDetails.checklist?.rollbackPlan?.length === 0) {
      setEditRollbackPlan(true)
    }
  }, [viewEvent.eventDetails.checklist])

  useEffect(() => {
    if (!initialLoad) return setInitialLoad(true)

    const timer = setTimeout(() => {
      handleSubmit()
    }, 1000)

    return () => clearTimeout(timer)
  }, [eventChecklist])

  return (
    <>
      <AccordionSetup title="Sjekkliste" icon={<FactCheckOutlinedIcon />} contentHeight={1000}>
        <Stack direction={'column'} sx={{
          rowGap: 2
        }}>
          <Stack>
            <EbCheckbox
              size="small"
              label="Godkjent tidspunkt"
              name="approvedTime"
              checked={eventChecklist.approvedTime}
              onChange={handleCheckboxChange}
            />
            <EbCheckbox
              size="small"
              label="Kundeliste er komplett"
              name="customerListCompleted"
              checked={eventChecklist.customerListCompleted}
              onChange={handleCheckboxChange}
            />
            <EbCheckbox
              size="small"
              label="Jobb er gjennomført"
              name="workCompleted"
              checked={eventChecklist.workCompleted}
              onChange={handleCheckboxChange}
            />
          </Stack>
          <Stack spacing={1}>
            <Stack direction={'row'} spacing={'auto'} sx={{
              alignItems: 'center'
            }}>
              <Typography variant="body2">Verifikasjon</Typography>
              {eventChecklist.verification.length !== 0 &&
                (editVerification ? (
                  <IconButton size="small" onClick={() => setEditVerification(false)}>
                    <CheckIcon color="success" />
                  </IconButton>
                ) : (
                  <IconButton size="small" onClick={() => setEditVerification(true)}>
                    <EditOutlinedIcon color="warning" />
                  </IconButton>
                ))}
            </Stack>
            {editVerification || eventChecklist.verification.length === 0 ? (
              <MonoInputField
                name="verification"
                placeholder="Skriv inn verifikasjon her"
                value={eventChecklist.verification}
                multiline
                variant="outlined"
                onChange={handleInputChange}
                onBlur={() => setEditVerification(false)}
              />
            ) : (
              <Box
                sx={{
                  bgcolor: theme.palette.bubble.yourInactive,
                  borderRadius: "12px",
                  padding: 1.5,
                  overflowWrap: 'anywhere',
                  transition: 'all 200ms ease-in',
                  cursor: 'pointer'
                }}>
                {verificationContentHeight > 150 ? (
                  <Collapse in={collapseVerification} collapsedSize={150}>
                    <TypographyWithUrl contentref={verificationContentSizeRef} onClick={handleCollapseVerification}>
                      {eventChecklist.verification}
                    </TypographyWithUrl>
                  </Collapse>
                ) : (
                  <TypographyWithUrl contentref={verificationContentSizeRef} onClick={handleCollapseVerification}>
                    {eventChecklist.verification}
                  </TypographyWithUrl>
                )}
                {verificationContentHeight > 150 && (
                  <Stack sx={{
                    pt: 1
                  }}>
                    <Link
                      color="primary"
                      sx={{
                        color: theme.palette.bubble.metaText,
                        textTransform: 'none',
                        fontSize: '13px',
                        padding: 0,
                      }}
                      onClick={handleCollapseVerification}
                    >
                      {collapseVerification ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Link>
                  </Stack>
                )}
              </Box>
            )}
            <InputField
              name="verificationLink"
              placeholder="URL - verifikasjon"
              value={eventChecklist.verificationLink}
              variant="standard"
              onChange={handleInputChange}
              icon={
                eventChecklist.verificationLink ? (
                  <IconButton href={eventChecklist.verificationLink} target="_blank" size="small">
                    <PublicOutlinedIcon fontSize="small" />
                  </IconButton>
                ) : undefined
              }
            />
          </Stack>
          <Stack spacing={1}>
            <Stack direction={'row'} spacing={'auto'} sx={{
              alignItems: 'center'
            }}>
              <Typography variant="body2">Rollback-plan</Typography>
              {eventChecklist.rollbackPlan.length !== 0 &&
                (editRollbackPlan ? (
                  <IconButton size="small" onClick={() => setEditRollbackPlan(false)}>
                    <CheckIcon color="success" />
                  </IconButton>
                ) : (
                  <IconButton size="small" onClick={() => setEditRollbackPlan(true)}>
                    <EditOutlinedIcon color="warning" />
                  </IconButton>
                ))}
            </Stack>
            {editRollbackPlan || eventChecklist.rollbackPlan.length === 0 ? (
              <MonoInputField
                name="rollbackPlan"
                placeholder="Skriv inn rollback-plan her"
                value={eventChecklist.rollbackPlan}
                multiline
                variant="outlined"
                onChange={handleInputChange}
                onBlur={() => setEditRollbackPlan(false)}
              />
            ) : (
              <Box
                sx={{
                  bgcolor: theme.palette.bubble.yourInactive,
                  borderRadius: "12px",
                  padding: 1.5,
                  overflowWrap: 'anywhere',
                  transition: 'all 200ms ease-in',
                  cursor: 'pointer'
                }}>
                {rollbackPlanContentHeight > 150 ? (
                  <Collapse in={collapseRollbackplan} collapsedSize={150}>
                    <TypographyWithUrl contentref={rollbackPlanContentSizeRef} onClick={handleCollapseRollbackPlan}>
                      {eventChecklist.rollbackPlan}
                    </TypographyWithUrl>
                  </Collapse>
                ) : (
                  <TypographyWithUrl contentref={rollbackPlanContentSizeRef} onClick={handleCollapseRollbackPlan}>
                    {eventChecklist.rollbackPlan}
                  </TypographyWithUrl>
                )}
                {rollbackPlanContentHeight > 150 && (
                  <Stack sx={{
                    pt: 1
                  }}>
                    <Link
                      color="primary"
                      sx={{
                        color: theme.palette.bubble.metaText,
                        textTransform: 'none',
                        fontSize: '13px',
                        padding: 0,
                        typography: {
                          paddingRight: 10,
                        },
                      }}
                      onClick={handleCollapseRollbackPlan}
                    >
                      {collapseRollbackplan ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Link>
                  </Stack>
                )}
              </Box>
            )}
            <InputField
              name="rollbackPlanLink"
              placeholder="URL - rollback-plan"
              value={eventChecklist.rollbackPlanLink}
              variant="standard"
              onChange={handleInputChange}
              icon={
                eventChecklist.rollbackPlanLink ? (
                  <IconButton href={eventChecklist.rollbackPlanLink} target="_blank" size="small">
                    <PublicOutlinedIcon fontSize="small" />
                  </IconButton>
                ) : undefined
              }
            />
          </Stack>
        </Stack>
      </AccordionSetup>
    </>
  );
}

export default ChecklistContainer
